import React from 'react';
import { createPortal } from 'react-dom';
import { Toaster as Sonner, toast, type ToasterProps as SonnerProps } from 'sonner';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { cn } from 'src/component/ds/utils';
import { DsThemedPortal, DS_TOAST_PORTAL_CLASS } from 'src/component/ds/DsRoot';
import { buttonVariants } from 'src/component/ds/ui/button';

/*
 * shadcn's Sonner (radix registry). Library choice: ADR-00006.
 * Deviations from shadcn:
 * - `unstyled` + classNames: sonner's styled rules outrank ds-* utilities.
 *   Sonner still owns stacking, swipe and motion.
 * - no `theme`: sonner's dark rules apply even unstyled; .ds-dark handles it.
 * - portalled onto the toast layer, since sonner renders inline.
 */

const TOAST_ICONS: SonnerProps['icons'] = {
  success: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} aria-hidden='true' />,
  error: <ErrorOutlineIcon sx={{ fontSize: 16 }} aria-hidden='true' />,
  info: <InfoOutlinedIcon sx={{ fontSize: 16 }} aria-hidden='true' />,
  loading: <AutorenewIcon sx={{ fontSize: 16 }} className='ds-animate-spin' aria-hidden='true' />,
  close: <CloseIcon sx={{ fontSize: 16 }} aria-hidden='true' />
};

/* Sonner sets data-type on each toast; `ds-group` lets the parts key off it. */
const TOAST_CLASSES: NonNullable<SonnerProps['toastOptions']>['classNames'] = {
  // 16/12 padding less the 1px border.
  toast: cn(
    'ds-group ds-flex ds-w-full ds-items-center ds-gap-3 ds-px-[15px] ds-py-[11px]',
    'ds-rounded ds-border ds-border-border ds-bg-card ds-text-foreground ds-shadow-sm'
  ),
  icon: cn(
    'ds-flex ds-shrink-0 ds-self-start ds-pt-0.5',
    'group-data-[type=success]:ds-text-success group-data-[type=error]:ds-text-destructive',
    'group-data-[type=info]:ds-text-info'
  ),
  content: 'ds-flex ds-min-w-0 ds-flex-1 ds-flex-col ds-gap-0.5 ds-break-words',
  title: 'ds-text-sm ds-font-medium group-data-[type=error]:ds-text-destructive',
  description: 'ds-text-sm ds-text-muted-foreground group-data-[type=error]:ds-text-destructive',
  // Row gap is 12px; the button sits 16px from the text.
  actionButton: cn(buttonVariants({ variant: 'outline' }), 'ds-ml-1 ds-shrink-0'),
  closeButton: cn(
    buttonVariants({ variant: 'ghost', size: 'icon-xs' }),
    'ds-absolute ds-right-1.5 ds-top-1.5 ds-bg-card ds-text-muted-foreground hover:ds-text-foreground',
    'ds-opacity-0 ds-transition-opacity group-hover:ds-opacity-100 group-focus-within:ds-opacity-100',
    'focus-visible:ds-opacity-100'
  )
};

export type ToasterProps = Omit<SonnerProps, 'theme'>;

/** Mounted once, in App; dsToast calls render here. */
function Toaster (props: ToasterProps) {
  return createPortal(
    <DsThemedPortal className={DS_TOAST_PORTAL_CLASS}>
      <Sonner
        position='top-right'
        offset={16}
        mobileOffset={16}
        gap={12}
        visibleToasts={3}
        closeButton
        icons={TOAST_ICONS}
        // Sonner sets --width inline, so only an inline value overrides it.
        style={{ '--width': '405px' } as React.CSSProperties}
        toastOptions={{ unstyled: true, classNames: TOAST_CLASSES }}
        {...props}
      />
    </DsThemedPortal>,
    document.body
  );
}

export type DsToastType = 'success' | 'error' | 'info' | 'loading';

/* Errors stay up longer: they usually need reading, not just noticing. */
export const DS_TOAST_DURATIONS: Record<DsToastType, number> = {
  success: 5000,
  info: 5000,
  error: 8000,
  // A background process: stays up until it is closed.
  loading: 0
};

export interface DsToastOptions {
  /** Optional button, e.g. Cancel on an export. Clicking it also dismisses. */
  action?: { label: React.ReactNode; onClick: () => void };
  /** Overrides the per-type duration; 0 keeps the toast up until closed. */
  timeout?: number;
}

type ShowToast = (title: React.ReactNode, description?: React.ReactNode, options?: DsToastOptions) => string | number;

function show (
  type: DsToastType,
  title: React.ReactNode,
  description?: React.ReactNode,
  { action, timeout = DS_TOAST_DURATIONS[type] }: DsToastOptions = {}
) {
  return toast[type](title, {
    description,
    duration: timeout === 0 ? Infinity : timeout,
    action,
    // The action button replaces the close button; it dismisses on click.
    closeButton: !action
  });
}

/**
 * Fire a v2 toast from anywhere, sagas included. Returns the toast id.
 * v1 screens keep using src/utils/toast (react-toastify).
 */
const dsToast: Record<DsToastType, ShowToast> & { close: (id?: string | number) => void } = {
  success: (title, description, options) => show('success', title, description, options),
  error: (title, description, options) => show('error', title, description, options),
  info: (title, description, options) => show('info', title, description, options),
  loading: (title, description, options) => show('loading', title, description, options),
  /** Closes one toast, or all of them when no id is given. */
  close: (id) => { toast.dismiss(id); }
};

export { Toaster, dsToast };
